/**
 * Procedural 16-Bit Audio & Dynamic BGM Synthesizer for "Bertahan Dari Anomali"
 * 
 * 100% Web Audio API programmatic sound generation.
 * Zero external audio files required, zero latency, 100% CC0 / MIT compliant and commercial safe.
 */

class AudioEngine {
  private ctx: AudioContext | null = null;
  private sfxGain: GainNode | null = null;
  private bgmGain: GainNode | null = null;
  private masterGain: GainNode | null = null;

  public sfxEnabled: boolean = true;
  public bgmEnabled: boolean = true;
  public sfxVolume: number = 0.8;
  public bgmVolume: number = 0.45;

  private isBgmPlaying: boolean = false;
  private bgmTimer: number | null = null;
  private currentStep: number = 0;

  constructor() {
    // Lazy initialized upon first user interaction
  }

  private initContext(): AudioContext | null {
    if (typeof window === "undefined") return null;
    if (!this.ctx) {
      const AudioCtxClass =
        window.AudioContext ||
        (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      if (AudioCtxClass) {
        this.ctx = new AudioCtxClass();
        this.masterGain = this.ctx.createGain();
        this.masterGain.gain.setValueAtTime(1.0, this.ctx.currentTime);
        this.masterGain.connect(this.ctx.destination);

        this.sfxGain = this.ctx.createGain();
        this.sfxGain.gain.setValueAtTime(this.sfxVolume, this.ctx.currentTime);
        this.sfxGain.connect(this.masterGain);

        this.bgmGain = this.ctx.createGain();
        this.bgmGain.gain.setValueAtTime(this.bgmVolume, this.ctx.currentTime);
        this.bgmGain.connect(this.masterGain);
      }
    }
    if (this.ctx && this.ctx.state === "suspended") {
      this.ctx.resume();
    }
    return this.ctx;
  }

  public setSfxVolume(vol: number) {
    this.sfxVolume = Math.max(0, Math.min(1, vol));
    if (this.sfxGain && this.ctx) {
      this.sfxGain.gain.setValueAtTime(this.sfxEnabled ? this.sfxVolume : 0, this.ctx.currentTime);
    }
  }

  public setBgmVolume(vol: number) {
    this.bgmVolume = Math.max(0, Math.min(1, vol));
    if (this.bgmGain && this.ctx) {
      this.bgmGain.gain.setValueAtTime(this.bgmEnabled ? this.bgmVolume : 0, this.ctx.currentTime);
    }
  }

  public toggleSfx(): boolean {
    this.sfxEnabled = !this.sfxEnabled;
    if (this.sfxGain && this.ctx) {
      this.sfxGain.gain.setValueAtTime(this.sfxEnabled ? this.sfxVolume : 0, this.ctx.currentTime);
    }
    if (this.sfxEnabled) {
      this.playSfx("ui_click");
    }
    return this.sfxEnabled;
  }

  public toggleBgm(): boolean {
    this.bgmEnabled = !this.bgmEnabled;
    if (this.bgmGain && this.ctx) {
      this.bgmGain.gain.setValueAtTime(this.bgmEnabled ? this.bgmVolume : 0, this.ctx.currentTime);
    }
    if (this.bgmEnabled && !this.isBgmPlaying) {
      this.startBgm();
    } else if (!this.bgmEnabled && this.isBgmPlaying) {
      this.stopBgm();
    }
    return this.bgmEnabled;
  }

  // ------------------------------------------------------------------------
  // PROCEDURAL BGM STEP SEQUENCER
  // "Ruins of Echoes" - 128 BPM Fast D-Minor Pentatonic Battle Rhythm
  // ------------------------------------------------------------------------
  public startBgm() {
    if (this.isBgmPlaying || !this.bgmEnabled) return;
    const ctx = this.initContext();
    if (!ctx) return;

    this.isBgmPlaying = true;
    this.currentStep = 0;

    const BPM = 128;
    const STEP_DURATION = 60 / BPM / 4; // 16th note step (~117ms)

    const N = {
      R: 0,
      D2: 73.42, F2: 87.31, G2: 98.0, A2: 110.0,
      C3: 130.81, D3: 146.83, E3: 164.81, F3: 174.61, G3: 196.0, A3: 220.0,
      C4: 261.63, D4: 293.66, E4: 329.63, F4: 349.23, G4: 392.0, A4: 440.0,
      C5: 523.25, D5: 587.33,
    };

    const BASS_LINE = [
      N.D2, N.D2, N.D3, N.D2, N.F2, N.D2, N.G2, N.A2,
      N.D2, N.D2, N.D3, N.D2, N.C3, N.D2, N.F2, N.G2,
      N.D2, N.D2, N.D3, N.D2, N.F2, N.D2, N.G2, N.A2,
      N.A2, N.G2, N.F2, N.G2, N.A2, N.C3, N.D3, N.R,
    ];

    const MELODY_LINE = [
      N.D4, N.R,  N.F4, N.G4, N.A4, N.R,  N.D5, N.R,
      N.C5, N.A4, N.R,  N.G4, N.F4, N.G4, N.A4, N.R,
      N.D4, N.R,  N.F4, N.G4, N.A4, N.C5, N.D5, N.R,
      N.D5, N.C5, N.A4, N.G4, N.F4, N.D4, N.R,  N.R,
    ];

    // 1: Kick, 2: Snare, 3: Hi-Hat, 4: Kick+Hat, 5: Snare+Hat
    const DRUM_LINE = [
      4, 3, 3, 3, 5, 3, 4, 3,
      4, 3, 4, 3, 5, 3, 3, 4,
      4, 3, 3, 3, 5, 3, 4, 3,
      5, 3, 4, 5, 5, 4, 5, 2,
    ];

    const schedule = () => {
      if (!this.isBgmPlaying || !this.bgmEnabled || !this.ctx || !this.bgmGain) return;
      const now = this.ctx.currentTime;
      const step = this.currentStep % 32;

      // Bass note
      const bFreq = BASS_LINE[step];
      if (bFreq > 0) {
        const osc = this.ctx.createOscillator();
        const gain = this.ctx.createGain();
        const filter = this.ctx.createBiquadFilter();

        osc.type = "sawtooth";
        osc.frequency.setValueAtTime(bFreq, now);

        filter.type = "lowpass";
        filter.frequency.setValueAtTime(420, now);

        gain.gain.setValueAtTime(0.24, now);
        gain.gain.exponentialRampToValueAtTime(0.001, now + STEP_DURATION * 0.95);

        osc.connect(filter);
        filter.connect(gain);
        gain.connect(this.bgmGain);

        osc.start(now);
        osc.stop(now + STEP_DURATION * 0.95);
      }

      // Melody note
      const mFreq = MELODY_LINE[step];
      if (mFreq > 0) {
        const osc = this.ctx.createOscillator();
        const gain = this.ctx.createGain();
        const filter = this.ctx.createBiquadFilter();

        osc.type = "square";
        osc.frequency.setValueAtTime(mFreq, now);

        filter.type = "lowpass";
        filter.frequency.setValueAtTime(2200, now);
        filter.frequency.exponentialRampToValueAtTime(750, now + STEP_DURATION * 1.5);

        gain.gain.setValueAtTime(0.14, now);
        gain.gain.exponentialRampToValueAtTime(0.001, now + STEP_DURATION * 1.4);

        osc.connect(filter);
        filter.connect(gain);
        gain.connect(this.bgmGain);

        osc.start(now);
        osc.stop(now + STEP_DURATION * 1.5);
      }

      // Drums
      const drum = DRUM_LINE[step];
      if (drum === 1 || drum === 4) {
        // Kick
        const osc = this.ctx.createOscillator();
        const gain = this.ctx.createGain();
        osc.type = "sine";
        osc.frequency.setValueAtTime(140, now);
        osc.frequency.exponentialRampToValueAtTime(32, now + 0.08);

        gain.gain.setValueAtTime(0.35, now);
        gain.gain.exponentialRampToValueAtTime(0.001, now + 0.09);

        osc.connect(gain);
        gain.connect(this.bgmGain);
        osc.start(now);
        osc.stop(now + 0.09);
      }
      if (drum === 2 || drum === 5) {
        // Snare
        const osc = this.ctx.createOscillator();
        const gain = this.ctx.createGain();
        osc.type = "triangle";
        osc.frequency.setValueAtTime(220, now);
        osc.frequency.exponentialRampToValueAtTime(60, now + 0.06);

        gain.gain.setValueAtTime(0.22, now);
        gain.gain.exponentialRampToValueAtTime(0.001, now + 0.07);

        osc.connect(gain);
        gain.connect(this.bgmGain);
        osc.start(now);
        osc.stop(now + 0.07);
      }
      if (drum === 3 || drum === 4 || drum === 5) {
        // Hat
        const osc = this.ctx.createOscillator();
        const gain = this.ctx.createGain();
        const filter = this.ctx.createBiquadFilter();

        osc.type = "square";
        osc.frequency.setValueAtTime(7500, now);

        filter.type = "highpass";
        filter.frequency.setValueAtTime(6500, now);

        gain.gain.setValueAtTime(0.05, now);
        gain.gain.exponentialRampToValueAtTime(0.001, now + 0.03);

        osc.connect(filter);
        filter.connect(gain);
        gain.connect(this.bgmGain);
        osc.start(now);
        osc.stop(now + 0.03);
      }

      this.currentStep++;
      this.bgmTimer = window.setTimeout(schedule, STEP_DURATION * 1000);
    };

    schedule();
  }

  public stopBgm() {
    this.isBgmPlaying = false;
    if (this.bgmTimer) {
      clearTimeout(this.bgmTimer);
      this.bgmTimer = null;
    }
  }

  // ------------------------------------------------------------------------
  // SOUND EFFECTS (SFX) SYNTHESIS
  // ------------------------------------------------------------------------
  public playSfx(
    type:
      | "slash_1"
      | "slash_2"
      | "slash_3"
      | "flash_step"
      | "crescent_slash"
      | "spin_strike"
      | "special_attack"
      | "target_lock"
      | "potion"
      | "hit_enemy"
      | "hit_player"
      | "enemy_death"
      | "telegraph_alert"
      | "ui_click"
      | "victory"
      | "game_over"
  ) {
    if (!this.sfxEnabled) return;
    const ctx = this.initContext();
    if (!ctx || !this.sfxGain) return;

    const now = ctx.currentTime;
    const microDetune = (Math.random() - 0.5) * 50;

    try {
      switch (type) {
        case "slash_1": {
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          const filter = ctx.createBiquadFilter();
          osc.type = "sawtooth";
          osc.frequency.setValueAtTime(650, now);
          osc.detune.setValueAtTime(microDetune, now);
          osc.frequency.exponentialRampToValueAtTime(160, now + 0.12);

          filter.type = "bandpass";
          filter.frequency.setValueAtTime(1400, now);
          filter.Q.setValueAtTime(2.5, now);

          gain.gain.setValueAtTime(0.28, now);
          gain.gain.exponentialRampToValueAtTime(0.001, now + 0.12);

          osc.connect(filter);
          filter.connect(gain);
          gain.connect(this.sfxGain);
          osc.start(now);
          osc.stop(now + 0.12);
          break;
        }

        case "slash_2": {
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          const filter = ctx.createBiquadFilter();
          osc.type = "sawtooth";
          osc.frequency.setValueAtTime(780, now);
          osc.detune.setValueAtTime(microDetune, now);
          osc.frequency.exponentialRampToValueAtTime(200, now + 0.13);

          filter.type = "bandpass";
          filter.frequency.setValueAtTime(1700, now);
          filter.Q.setValueAtTime(2.5, now);

          gain.gain.setValueAtTime(0.32, now);
          gain.gain.exponentialRampToValueAtTime(0.001, now + 0.13);

          osc.connect(filter);
          filter.connect(gain);
          gain.connect(this.sfxGain);
          osc.start(now);
          osc.stop(now + 0.13);
          break;
        }

        case "slash_3": {
          // Heavy finisher slash
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          const filter = ctx.createBiquadFilter();
          osc.type = "sawtooth";
          osc.frequency.setValueAtTime(950, now);
          osc.detune.setValueAtTime(microDetune, now);
          osc.frequency.exponentialRampToValueAtTime(110, now + 0.18);

          filter.type = "bandpass";
          filter.frequency.setValueAtTime(2100, now);
          filter.Q.setValueAtTime(3.0, now);

          gain.gain.setValueAtTime(0.42, now);
          gain.gain.exponentialRampToValueAtTime(0.001, now + 0.18);

          osc.connect(filter);
          filter.connect(gain);
          gain.connect(this.sfxGain);
          osc.start(now);
          osc.stop(now + 0.18);
          break;
        }

        case "flash_step": {
          // Sharp wind rush + lightning sheen
          const osc1 = ctx.createOscillator();
          const osc2 = ctx.createOscillator();
          const gain = ctx.createGain();

          osc1.type = "sine";
          osc1.frequency.setValueAtTime(520, now);
          osc1.frequency.exponentialRampToValueAtTime(1450, now + 0.14);

          osc2.type = "triangle";
          osc2.frequency.setValueAtTime(880, now);
          osc2.frequency.exponentialRampToValueAtTime(2100, now + 0.16);

          gain.gain.setValueAtTime(0.35, now);
          gain.gain.exponentialRampToValueAtTime(0.001, now + 0.16);

          osc1.connect(gain);
          osc2.connect(gain);
          gain.connect(this.sfxGain);

          osc1.start(now);
          osc2.start(now);
          osc1.stop(now + 0.16);
          osc2.stop(now + 0.16);
          break;
        }

        case "crescent_slash": {
          // Resonant energy wave
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          const filter = ctx.createBiquadFilter();

          osc.type = "sawtooth";
          osc.frequency.setValueAtTime(340, now);
          osc.frequency.exponentialRampToValueAtTime(1150, now + 0.18);
          osc.frequency.exponentialRampToValueAtTime(280, now + 0.28);

          filter.type = "bandpass";
          filter.frequency.setValueAtTime(1800, now);
          filter.Q.setValueAtTime(4.0, now);

          gain.gain.setValueAtTime(0.38, now);
          gain.gain.exponentialRampToValueAtTime(0.001, now + 0.28);

          osc.connect(filter);
          filter.connect(gain);
          gain.connect(this.sfxGain);

          osc.start(now);
          osc.stop(now + 0.28);
          break;
        }

        case "spin_strike": {
          // Whirlwind circular sweep
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          const filter = ctx.createBiquadFilter();

          osc.type = "sawtooth";
          osc.frequency.setValueAtTime(220, now);
          osc.frequency.exponentialRampToValueAtTime(800, now + 0.14);
          osc.frequency.exponentialRampToValueAtTime(140, now + 0.26);

          filter.type = "lowpass";
          filter.frequency.setValueAtTime(1200, now);

          gain.gain.setValueAtTime(0.4, now);
          gain.gain.exponentialRampToValueAtTime(0.001, now + 0.26);

          osc.connect(filter);
          filter.connect(gain);
          gain.connect(this.sfxGain);

          osc.start(now);
          osc.stop(now + 0.26);
          break;
        }

        case "special_attack": {
          // Devastating charge + shockwave explosion
          // Sub-bass hit
          const oscSub = ctx.createOscillator();
          const gainSub = ctx.createGain();
          oscSub.type = "sine";
          oscSub.frequency.setValueAtTime(180, now);
          oscSub.frequency.exponentialRampToValueAtTime(25, now + 0.45);
          gainSub.gain.setValueAtTime(0.55, now);
          gainSub.gain.exponentialRampToValueAtTime(0.001, now + 0.45);
          oscSub.connect(gainSub);
          gainSub.connect(this.sfxGain);
          oscSub.start(now);
          oscSub.stop(now + 0.45);

          // Metallic blade impact
          const oscHigh = ctx.createOscillator();
          const gainHigh = ctx.createGain();
          oscHigh.type = "sawtooth";
          oscHigh.frequency.setValueAtTime(1200, now);
          oscHigh.frequency.exponentialRampToValueAtTime(120, now + 0.35);
          gainHigh.gain.setValueAtTime(0.4, now);
          gainHigh.gain.exponentialRampToValueAtTime(0.001, now + 0.35);
          oscHigh.connect(gainHigh);
          gainHigh.connect(this.sfxGain);
          oscHigh.start(now);
          oscHigh.stop(now + 0.35);
          break;
        }

        case "target_lock": {
          // Clean high-tech lock chime (1760 Hz & 3520 Hz)
          [1760, 3520].forEach((freq) => {
            const osc = ctx.createOscillator();
            const gain = ctx.createGain();
            osc.type = "sine";
            osc.frequency.setValueAtTime(freq, now);

            gain.gain.setValueAtTime(0.18, now);
            gain.gain.exponentialRampToValueAtTime(0.001, now + 0.18);

            osc.connect(gain);
            gain.connect(this.sfxGain!);
            osc.start(now);
            osc.stop(now + 0.18);
          });
          break;
        }

        case "potion": {
          // Ascending healing sparkle arpeggio
          const notes = [440, 554.37, 659.25, 880];
          notes.forEach((freq, idx) => {
            const osc = ctx.createOscillator();
            const gain = ctx.createGain();
            const t = now + idx * 0.05;

            osc.type = "sine";
            osc.frequency.setValueAtTime(freq, t);

            gain.gain.setValueAtTime(0.2, t);
            gain.gain.exponentialRampToValueAtTime(0.001, t + 0.18);

            osc.connect(gain);
            gain.connect(this.sfxGain!);
            osc.start(t);
            osc.stop(t + 0.18);
          });
          break;
        }

        case "hit_enemy": {
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          osc.type = "triangle";
          osc.frequency.setValueAtTime(260, now);
          osc.detune.setValueAtTime(microDetune, now);
          osc.frequency.exponentialRampToValueAtTime(45, now + 0.12);

          gain.gain.setValueAtTime(0.35, now);
          gain.gain.exponentialRampToValueAtTime(0.001, now + 0.12);

          osc.connect(gain);
          gain.connect(this.sfxGain);
          osc.start(now);
          osc.stop(now + 0.12);
          break;
        }

        case "hit_player": {
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          osc.type = "sawtooth";
          osc.frequency.setValueAtTime(140, now);
          osc.detune.setValueAtTime(microDetune, now);
          osc.frequency.exponentialRampToValueAtTime(30, now + 0.2);

          gain.gain.setValueAtTime(0.45, now);
          gain.gain.exponentialRampToValueAtTime(0.001, now + 0.2);

          osc.connect(gain);
          gain.connect(this.sfxGain);
          osc.start(now);
          osc.stop(now + 0.2);
          break;
        }

        case "enemy_death": {
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          osc.type = "sawtooth";
          osc.frequency.setValueAtTime(240, now);
          osc.frequency.exponentialRampToValueAtTime(30, now + 0.35);

          gain.gain.setValueAtTime(0.3, now);
          gain.gain.exponentialRampToValueAtTime(0.001, now + 0.35);

          osc.connect(gain);
          gain.connect(this.sfxGain);
          osc.start(now);
          osc.stop(now + 0.35);
          break;
        }

        case "telegraph_alert": {
          // Ominous low hum warning indicator
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          osc.type = "sine";
          osc.frequency.setValueAtTime(80, now);
          osc.frequency.linearRampToValueAtTime(120, now + 0.15);

          gain.gain.setValueAtTime(0.18, now);
          gain.gain.exponentialRampToValueAtTime(0.001, now + 0.18);

          osc.connect(gain);
          gain.connect(this.sfxGain);
          osc.start(now);
          osc.stop(now + 0.18);
          break;
        }

        case "ui_click": {
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          osc.type = "square";
          osc.frequency.setValueAtTime(920, now);
          gain.gain.setValueAtTime(0.12, now);
          gain.gain.exponentialRampToValueAtTime(0.001, now + 0.04);

          osc.connect(gain);
          gain.connect(this.sfxGain);
          osc.start(now);
          osc.stop(now + 0.04);
          break;
        }

        case "victory": {
          const chords = [523.25, 659.25, 783.99, 1046.5]; // C Major arpeggio
          chords.forEach((f, i) => {
            const osc = ctx.createOscillator();
            const gain = ctx.createGain();
            const t = now + i * 0.12;
            osc.type = "triangle";
            osc.frequency.setValueAtTime(f, t);
            gain.gain.setValueAtTime(0.28, t);
            gain.gain.exponentialRampToValueAtTime(0.001, t + 0.4);
            osc.connect(gain);
            gain.connect(this.sfxGain!);
            osc.start(t);
            osc.stop(t + 0.4);
          });
          break;
        }

        case "game_over": {
          const notes = [293.66, 261.63, 220.0, 146.83];
          notes.forEach((f, i) => {
            const osc = ctx.createOscillator();
            const gain = ctx.createGain();
            const t = now + i * 0.22;
            osc.type = "sawtooth";
            osc.frequency.setValueAtTime(f, t);
            gain.gain.setValueAtTime(0.25, t);
            gain.gain.exponentialRampToValueAtTime(0.001, t + 0.35);
            osc.connect(gain);
            gain.connect(this.sfxGain!);
            osc.start(t);
            osc.stop(t + 0.35);
          });
          break;
        }
      }
    } catch {
      // Ignore browser autoplay policy restrictions until first interaction
    }
  }
}

export const anomaliAudio = new AudioEngine();
